import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { jobs, trials, shortlist as shortlistApi, auth, company as companyApi, payments } from '../utils/api';
import { Briefcase, Plus, Users, Clock, CheckCircle, MessageSquare, LogOut, ArrowRight, Trash2, List, Settings, X, User, Wallet, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import EditBookingModal from '../components/EditBookingModal';

const firstName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return 'Candidate';
    return fullName.trim().split(/\s+/)[0];
};

const humaniseStatus = (status) => {
    if (!status || typeof status !== 'string') return '';
    return status
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Human-readable labels for booking lifecycle stages
const TRIAL_STATUS_LABELS = {
    pending_candidate_response: 'Awaiting candidate response',
    pending_payment: 'Awaiting payment',
    payment_failed: 'Payment failed',
    scheduled: 'Scheduled',
    in_progress: 'In progress',
    completed: 'Completed',
    terminated_early: 'Ended early',
    cancelled: 'Cancelled',
    cancelled_late: 'Cancelled (late)',
    expired: 'Expired',
};

const trialStatusLabel = (status) => TRIAL_STATUS_LABELS[status] || humaniseStatus(status);

const formatDate = (value) => {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const CompanyDashboard = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [myJobs, setMyJobs] = useState([]);
    const [myTrials, setMyTrials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [activeSection, setActiveSection] = useState('jobs');
    const [shortlistedCandidates, setShortlistedCandidates] = useState([]);
    const [showAccountMenu, setShowAccountMenu] = useState(false);
    const [showDeleteAccount, setShowDeleteAccount] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);
    const [creditBalance, setCreditBalance] = useState({ pence: 0, display: '£0.00' });
    const [showCreditTooltip, setShowCreditTooltip] = useState(false);
    const [payingBookingId, setPayingBookingId] = useState(null);
    const [paymentModal, setPaymentModal] = useState(null); // { booking, total, applyCredit, creditPence }
    const [employerCancelModal, setEmployerCancelModal] = useState(null);
    const [cancellingEmployer, setCancellingEmployer] = useState(false);
    const [editBookingModal, setEditBookingModal] = useState(null);
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [jobsRes, trialsRes, slRes] = await Promise.all([
                jobs.getAll(),
                trials.getMyBookings(),
                shortlistApi.getAll()
            ]);
            setMyJobs(jobsRes.data.filter(j => j.company_id === user.id));
            setMyTrials(trialsRes.data.bookings || []);
            setShortlistedCandidates(slRes.data.shortlist || []);
        } catch (error) {
            console.error('Dashboard load error:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
        // Credit balance loads independently so a failure here never blocks the dashboard.
        try {
            const res = await companyApi.getCreditBalance();
            setCreditBalance({
                pence: res.data.platform_credit_pence || 0,
                display: res.data.platform_credit_display || '£0.00',
            });
        } catch (err) {
            // Silent: indicator simply stays hidden.
        }
    };

    const openPaymentModal = async (booking) => {
        try {
            const res = await payments.calculate(booking.trial_days);
            const total = res.data.total; // pounds (float)
            setPaymentModal({
                booking,
                total,
                applyCredit: false,   // employer must explicitly opt in to use credit
                creditPence: creditBalance.pence,
            });
        } catch (err) {
            toast.error(err.userMessage || 'Could not load payment details');
        }
    };

    const handleConfirmPayment = async () => {
        if (!paymentModal) return;
        const { booking, applyCredit } = paymentModal;
        setPayingBookingId(booking.id);
        try {
            const origin = window.location.origin;
            const res = await payments.createCheckout(booking.id, origin, applyCredit);
            if (res.data.fully_paid_with_credit) {
                toast.success('Trial booked — paid in full with platform credit.');
                setPaymentModal(null);
                await loadData();
            } else if (res.data.checkout_url) {
                window.location.href = res.data.checkout_url;
            } else {
                toast.error('Payment could not be initiated');
            }
        } catch (err) {
            toast.error(err.userMessage || 'Failed to initiate payment');
        } finally {
            setPayingBookingId(null);
        }
    };

    const handleEmployerCancelConfirm = async () => {
        if (!employerCancelModal) return;
        setCancellingEmployer(true);
        try {
            const res = await trials.employerCancel(employerCancelModal.id, '');
            toast.success(res.data?.message || 'Trial cancelled');
            setEmployerCancelModal(null);
            await loadData();
        } catch (err) {
            toast.error(err.userMessage || 'Failed to cancel trial');
        } finally {
            setCancellingEmployer(false);
        }
    };

    const handleEditSubmit = async (changes, message) => {
        if (!editBookingModal) return;
        setEditing(true);
        try {
            await trials.requestEdit(editBookingModal.id, changes, message);
            toast.success('Edit request sent — the candidate will see it on their dashboard.');
            setEditBookingModal(null);
            await loadData();
        } catch (err) {
            toast.error(err.userMessage || 'Failed to send edit request');
        } finally {
            setEditing(false);
        }
    };

    // Highlight the messaging icon when a candidate-initiated edit is awaiting response.
    const hasUnreadEditRequest = myTrials.some(t =>
        Array.isArray(t.edit_requests) &&
        t.edit_requests.some(r => r.status === 'pending' && r.initiated_by === 'candidate')
    );

    const handleDeleteJob = async (jobId, e) => {
        e.stopPropagation();
        if (deleteConfirm === jobId) {
            setDeleting(true);
            try {
                await jobs.delete(jobId);
                setMyJobs(prev => prev.filter(j => j.id !== jobId));
                toast.success('Job deleted successfully');
                setDeleteConfirm(null);
            } catch (error) {
                toast.error(error.userMessage || 'Failed to delete job');
            } finally {
                setDeleting(false);
            }
        } else {
            setDeleteConfirm(jobId);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
        toast.success('Logged out successfully');
    };

    const handleDeleteAccount = async () => {
        setDeletingAccount(true);
        try {
            await auth.deleteAccount();
            logout();
            navigate('/');
            toast.success('Account deleted successfully');
        } catch (error) {
            toast.error('Failed to delete account');
        } finally {
            setDeletingAccount(false);
            setShowDeleteAccount(false);
        }
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF4' }}>
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#8B5CF6] border-t-transparent" />
        </div>
    );

    const activeTrials = myTrials.filter(t => ['scheduled', 'in_progress', 'active'].includes(t.status));
    const completedTrials = myTrials.filter(t => t.status === 'completed');
    const pendingTrials = myTrials.filter(t => ['pending', 'pending_candidate_response', 'pending_payment'].includes(t.status));

    const stats = [
        { label: 'Active Jobs', value: myJobs.filter(j => j.status === 'active').length, icon: Briefcase, color: '#8B5CF6' },
        { label: 'Active Trials', value: activeTrials.length, icon: Clock, color: '#10B981' },
        { label: 'Pending', value: pendingTrials.length, icon: Users, color: '#EC4899' },
        { label: 'Completed', value: completedTrials.length, icon: CheckCircle, color: '#34D399' },
    ];

    const navItems = [
        { id: 'jobs', label: 'Active Jobs', icon: Briefcase },
        { id: 'pending-bookings', label: 'Pending Bookings', icon: Clock, count: pendingTrials.length },
        { id: 'active-trials', label: 'Active Trials', icon: Clock },
        { id: 'completed', label: 'Trials Completed', icon: CheckCircle },
        { id: 'shortlist', label: 'Shortlist', icon: List, count: shortlistedCandidates.length },
    ];

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <header className="sticky top-0 z-50 bg-[#F0FDF4]/80 backdrop-blur-xl border-b border-purple-200/50 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
                        <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                        <span className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</span>
                    </div>
                    <div className="flex items-center space-x-3">
                        {creditBalance.pence > 0 && (
                            <div
                                className="relative"
                                onMouseEnter={() => setShowCreditTooltip(true)}
                                onMouseLeave={() => setShowCreditTooltip(false)}
                            >
                                <button
                                    type="button"
                                    onClick={() => setShowCreditTooltip(v => !v)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-50 text-[#7C3AED] border border-purple-200/70 hover:bg-purple-100 transition-colors text-xs font-semibold"
                                    style={{ fontFamily: 'Unbounded, sans-serif' }}
                                    data-testid="credit-balance-indicator"
                                >
                                    <Wallet className="h-3.5 w-3.5" />
                                    Credits: {creditBalance.display}
                                </button>
                                {showCreditTooltip && (
                                    <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-purple-200/50 p-3 z-50" data-testid="credit-balance-tooltip">
                                        <p className="text-xs text-slate-600 leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                            Platform credit can be used against future trial shift bookings.
                                        </p>
                                        {user?.is_demo && (
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        const res = await companyApi.resetDemoCredit();
                                                        setCreditBalance({ pence: res.data.platform_credit_pence, display: res.data.platform_credit_display });
                                                        toast.success('Demo credit refilled to £5,000');
                                                        setShowCreditTooltip(false);
                                                    } catch {
                                                        toast.error('Could not refill credit');
                                                    }
                                                }}
                                                className="mt-2 w-full text-xs font-semibold text-[#7C3AED] border border-purple-200 rounded-lg py-1.5 hover:bg-purple-50 transition-colors"
                                                data-testid="refill-demo-credit-btn"
                                            >
                                                Refill to £5,000
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        <Button variant="ghost" className="text-slate-600 hover:text-[#8B5CF6] hover:bg-purple-50" onClick={() => navigate('/messages')} data-testid="messages-btn">
                            <MessageSquare className="h-5 w-5" strokeWidth={2} />
                        </Button>
                        <div className="relative">
                            <Button variant="ghost" className="text-slate-600 hover:text-[#8B5CF6] hover:bg-purple-50" onClick={() => setShowAccountMenu(!showAccountMenu)} data-testid="account-settings-btn">
                                <Settings className="h-5 w-5" strokeWidth={2} />
                            </Button>
                            {showAccountMenu && (
                                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-purple-200/50 py-2 z-50">
                                    <button onClick={() => { setShowAccountMenu(false); handleLogout(); }}
                                        className="w-full px-4 py-2.5 text-left text-sm text-slate-600 hover:bg-purple-50 hover:text-[#8B5CF6] flex items-center gap-2"
                                        data-testid="menu-logout">
                                        <LogOut className="h-4 w-4" /> Log Out
                                    </button>
                                    <button onClick={() => { setShowAccountMenu(false); setShowDeleteAccount(true); }}
                                        className="w-full px-4 py-2.5 text-left text-sm text-red-500 hover:bg-red-50 flex items-center gap-2"
                                        data-testid="menu-delete-account">
                                        <Trash2 className="h-4 w-4" /> Delete Account
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-10">
                {/* Welcome */}
                <div className="mb-10">
                    <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        Employer Dashboard
                    </span>
                    <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="dashboard-heading">
                        Welcome, {user.company_name || firstName(user.name)}
                    </h1>
                    <p className="text-slate-500 mt-1" style={{ fontFamily: 'Barlow, sans-serif' }}>Manage your jobs and trials</p>
                </div>

                {/* Stats Block */}
                <div className="rounded-2xl p-6 md:p-8 mb-10" style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4C1D95 100%)' }}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {stats.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div key={i} data-testid={['active-jobs-stat', 'active-trials-stat', 'pending-responses-stat', 'completed-trials-stat'][i]}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Icon className="h-4 w-4" style={{ color: s.color }} strokeWidth={2.5} />
                                        <span className="text-xs font-semibold text-purple-300 uppercase tracking-wider" style={{ fontFamily: 'Unbounded, sans-serif' }}>{s.label}</span>
                                    </div>
                                    <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>{s.value}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Section Nav Pills + Post New Job */}
                <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                    <div className="flex items-center gap-2 flex-wrap" data-testid="section-nav">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = activeSection === item.id;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveSection(item.id)}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm transition-all duration-200 ${
                                        isActive
                                            ? 'text-white shadow-md'
                                            : 'bg-white text-slate-600 border border-purple-200/50 hover:border-purple-400 hover:text-[#8B5CF6] shadow-sm'
                                    }`}
                                    style={isActive ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)' } : {}}
                                    data-testid={`nav-${item.id}`}
                                >
                                    <Icon className="h-4 w-4" strokeWidth={2.5} />
                                    <span style={{ fontFamily: 'Unbounded, sans-serif' }}>{item.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    {activeSection === 'jobs' && (
                        <Button className="btn-primary" onClick={() => navigate('/company/post-job')} data-testid="post-job-btn">
                            <Plus className="mr-2 h-4 w-4" /> Post New Job
                        </Button>
                    )}
                </div>

                {/* === SECTION CONTENT === */}

                {/* Active Jobs */}
                {activeSection === 'jobs' && (
                    <div>
                        {myJobs.length === 0 ? (
                            <div className="bg-white rounded-2xl p-12 text-center border border-purple-200/50 shadow-sm" data-testid="no-jobs-message">
                                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#F3E8FF' }}>
                                    <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                                </div>
                                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>No jobs yet</h3>
                                <p className="text-slate-500 mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>Post your first job to start finding candidates</p>
                                <Button className="btn-primary" onClick={() => navigate('/company/post-job')}>Post Your First Job</Button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4">
                                {myJobs.map(job => (
                                    <div key={job.id} className="bg-white rounded-2xl p-6 border border-purple-200/50 shadow-sm hover:shadow-md hover:border-purple-300 transition-all duration-200 cursor-pointer group" onClick={() => navigate(`/company/jobs/${job.id}`)} data-testid={`job-card-${job.id}`}>
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-xl font-bold mb-2 group-hover:text-[#8B5CF6] transition-colors" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>{job.title}</h3>
                                                <p className="text-slate-500 mb-3 text-sm leading-relaxed truncate" style={{ fontFamily: 'Barlow, sans-serif' }}>{job.description?.substring(0, 150)}...</p>
                                                <div className="flex items-center flex-wrap gap-3 text-sm">
                                                    <span className="font-semibold text-[#10B981]" style={{ fontFamily: 'Unbounded, sans-serif' }}>£{job.pay_rate}/hr</span>
                                                    {job.salary && <span className="text-[#8B5CF6] font-semibold">{job.salary}</span>}
                                                    <span className="text-slate-400">{job.trial_length_days} days</span>
                                                    <span className="text-slate-400">{job.location}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                    job.status === 'active'
                                                        ? 'bg-[#ECFDF5] text-[#059669]'
                                                        : 'bg-slate-100 text-slate-500'
                                                }`} style={{ fontFamily: 'Unbounded, sans-serif' }}>{humaniseStatus(job.status)}</span>
                                                <button
                                                    onClick={(e) => handleDeleteJob(job.id, e)}
                                                    className={`p-2 rounded-lg transition-all duration-200 ${
                                                        deleteConfirm === job.id
                                                            ? 'bg-red-100 text-red-600 ring-2 ring-red-300'
                                                            : 'text-slate-300 hover:text-red-500 hover:bg-red-50'
                                                    }`}
                                                    disabled={deleting}
                                                    data-testid={`delete-job-${job.id}`}
                                                    title={deleteConfirm === job.id ? 'Click again to confirm deletion' : 'Delete job'}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                                {deleteConfirm === job.id && (
                                                    <span className="text-xs text-red-500 font-semibold whitespace-nowrap" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                        Confirm?
                                                    </span>
                                                )}
                                                <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-[#8B5CF6] transition-colors" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Pending Bookings (awaiting candidate response / payment) */}
                {activeSection === 'pending-bookings' && (
                    <div>
                        {pendingTrials.length === 0 ? (
                            <div className="bg-white rounded-2xl p-8 text-center border border-purple-200/50 shadow-sm" data-testid="pending-bookings-empty">
                                <p className="text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>No pending booking requests</p>
                                <p className="text-xs text-slate-400 mt-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                    Requests that are awaiting a candidate response or payment will appear here.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4">
                                {pendingTrials.map(trial => {
                                    const isAwaitingPayment = trial.status === 'pending_payment';
                                    return (
                                        <div key={trial.id} className="bg-white rounded-2xl p-6 border border-purple-200/50 shadow-sm" data-testid={`pending-booking-card-${trial.id}`}>
                                            <div className="flex items-center justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-bold mb-1" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>{firstName(trial.candidate_name)}</h4>
                                                    <p className="text-sm text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>{trial.job_title || 'Position'}</p>
                                                    {trial.start_date && (
                                                        <p className="text-xs text-slate-400 mt-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                            Start: {formatDate(trial.start_date)} &middot; {trial.trial_days} day{trial.trial_days !== 1 ? 's' : ''}
                                                        </p>
                                                    )}
                                                    {trial.response_deadline && trial.status === 'pending_candidate_response' && (
                                                        <p className="text-xs text-amber-600 mt-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                            Candidate has until {formatDate(trial.response_deadline)} to respond.
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex flex-col items-end gap-2">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                        isAwaitingPayment ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'
                                                    }`} style={{ fontFamily: 'Unbounded, sans-serif' }} data-testid={`pending-booking-status-${trial.id}`}>
                                                        {trialStatusLabel(trial.status)}
                                                    </span>
                                                    {isAwaitingPayment && trial.stripe_session_id && (
                                                        <p className="text-xs text-slate-500 max-w-[12rem] text-right" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                                            Check your email for the payment link.
                                                        </p>
                                                    )}
                                                    {isAwaitingPayment && (
                                                        <Button
                                                            className="bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white hover:opacity-90 text-xs px-4 py-1 h-auto"
                                                            onClick={() => openPaymentModal(trial)}
                                                            data-testid={`review-pay-btn-${trial.id}`}
                                                        >
                                                            Review &amp; Pay
                                                        </Button>
                                                    )}
                                                    {trial.status === 'pending_candidate_response' && (
                                                        <Button variant="outline" className="rounded-full border-purple-200 text-[#7C3AED] hover:bg-purple-50 text-xs px-4 py-1 h-auto"
                                                            onClick={() => setEditBookingModal(trial)}
                                                            data-testid={`edit-btn-${trial.id}`}>
                                                            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                                                        </Button>
                                                    )}
                                                    <Button variant="outline" className="rounded-full border-purple-200 text-slate-600 hover:border-red-300 hover:bg-red-50 hover:text-red-600 text-xs px-4 py-1 h-auto"
                                                        onClick={() => setEmployerCancelModal(trial)}
                                                        data-testid={`employer-cancel-btn-${trial.id}`}>
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Active Trials */}
                {activeSection === 'active-trials' && (
                    <div>
                        {activeTrials.length === 0 ? (
                            <div className="bg-white rounded-2xl p-8 text-center border border-purple-200/50 shadow-sm">
                                <p className="text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>No active trials at the moment</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4">
                                {activeTrials.map(trial => (
                                    <div key={trial.id} className="bg-white rounded-2xl p-6 border border-purple-200/50 shadow-sm hover:shadow-md transition-all duration-200" data-testid={`active-trial-card-${trial.id}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="cursor-pointer flex-1" onClick={() => navigate('/company/trials')}>
                                                <h4 className="font-bold mb-1" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>{firstName(trial.candidate_name)}</h4>
                                                <p className="text-sm text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>{trial.job_title || 'Position'}</p>
                                                {trial.start_date && (
                                                    <p className="text-xs text-slate-400 mt-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                        Started: {formatDate(trial.start_date)} &middot; Day {trial.days_completed || 0} of {trial.trial_days}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#ECFDF5] text-[#059669]" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                    {trialStatusLabel(trial.status)}
                                                </span>
                                                <Button variant="outline" className="rounded-full border-purple-200 text-slate-600 hover:border-red-300 hover:bg-red-50 hover:text-red-600 text-xs"
                                                    onClick={(e) => { e.stopPropagation(); setEmployerCancelModal(trial); }}
                                                    data-testid={`employer-cancel-btn-${trial.id}`}>
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Trials Completed */}
                {activeSection === 'completed' && (
                    <div>
                        {completedTrials.length === 0 ? (
                            <div className="bg-white rounded-2xl p-8 text-center border border-purple-200/50 shadow-sm">
                                <p className="text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>No completed trials yet</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4">
                                {completedTrials.map(trial => (
                                    <div key={trial.id} className="bg-white rounded-2xl p-6 border border-purple-200/50 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer" onClick={() => navigate('/company/trials')} data-testid={`completed-trial-card-${trial.id}`}>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h4 className="font-bold mb-1" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>{firstName(trial.candidate_name)}</h4>
                                                <p className="text-sm text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>{trial.job_title || 'Position'}</p>
                                                {trial.start_date && (
                                                    <p className="text-xs text-slate-400 mt-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                        {trial.trial_days} day trial &middot; {trial.has_feedback ? 'Feedback submitted' : 'Awaiting feedback'}
                                                    </p>
                                                )}
                                            </div>
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                completed
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Shortlist */}
                {activeSection === 'shortlist' && (
                    <div>
                        {shortlistedCandidates.length === 0 ? (
                            <div className="bg-white rounded-2xl p-8 text-center border border-purple-200/50 shadow-sm" data-testid="shortlist-empty">
                                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#F3E8FF' }}>
                                    <List className="h-8 w-8" style={{ color: '#9333EA' }} />
                                </div>
                                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>No shortlisted candidates yet</h3>
                                <p className="text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                    Shortlisted candidates will appear here, grouped by job title
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {Object.entries(
                                    shortlistedCandidates.reduce((groups, entry) => {
                                        const title = entry.job_title || 'Untitled Job';
                                        if (!groups[title]) groups[title] = { job_id: entry.job_id, candidates: [] };
                                        groups[title].candidates.push(entry);
                                        return groups;
                                    }, {})
                                ).map(([jobTitle, group]) => (
                                    <div key={jobTitle}>
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>{jobTitle}</h3>
                                            <button onClick={() => navigate(`/company/jobs/${group.job_id}`)}
                                                className="text-sm text-[#8B5CF6] hover:underline font-semibold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                View Job
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {group.candidates.map(entry => (
                                                <div key={entry.id} className="bg-white rounded-2xl p-5 border border-purple-200/50 shadow-sm hover:shadow-md transition-all"
                                                    data-testid={`shortlist-card-${entry.id}`}>
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F3E8FF' }}>
                                                                <User className="h-5 w-5" style={{ color: '#9333EA' }} />
                                                            </div>
                                                            <div>
                                                                <h4 className="font-bold text-sm" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>{firstName(entry.candidate_name)}</h4>
                                                                <p className="text-xs text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                                                    {entry.candidate_snapshot?.primary_role || ''}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {entry.candidate_snapshot?.is_wildcard && (
                                                            <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'linear-gradient(135deg, #e2e8f0, #94a3b8)', color: '#475569' }}>
                                                                Wildcard
                                                            </span>
                                                        )}
                                                    </div>
                                                    {entry.candidate_snapshot?.top_skills?.length > 0 && (
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {entry.candidate_snapshot.top_skills.slice(0, 3).map((skill, i) => (
                                                                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-[#8B5CF6]" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                                    {skill}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>
            
            {/* Edit Booking Modal */}
            {editBookingModal && (
                <EditBookingModal
                    booking={editBookingModal}
                    submitting={editing}
                    onClose={() => setEditBookingModal(null)}
                    onSubmit={handleEditSubmit}
                />
            )}

            {/* Employer Cancel Modal */}
            {employerCancelModal && (() => {
                const startDt = employerCancelModal.start_date ? new Date(employerCancelModal.start_date) : null;
                const hoursUntilStart = startDt ? Math.max((startDt.getTime() - Date.now()) / 36e5, 0) : null;
                const late = hoursUntilStart !== null && hoursUntilStart < 48 && employerCancelModal.payment_status === 'paid';
                return (
                    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => !cancellingEmployer && setEmployerCancelModal(null)}>
                        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="employer-cancel-modal">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                    <X className="h-5 w-5 text-amber-600" />
                                </div>
                                <h3 className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Cancel trial</h3>
                            </div>
                            <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                You're cancelling the trial with <strong>{employerCancelModal.candidate_name || 'this candidate'}</strong> for <strong>{employerCancelModal.job_title || 'this role'}</strong>.
                            </p>
                            <div className="rounded-xl bg-purple-50 border border-purple-200/60 p-4 mb-4 text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                <p className="font-semibold text-[#0A0A0A] mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>Cancellation policy</p>
                                <ul className="list-disc list-inside space-y-1 text-slate-600">
                                    <li>48+ hours notice — full refund to your original payment method.</li>
                                    <li>Within 48 hours — no refund is issued.</li>
                                </ul>
                                {late && (
                                    <p className="mt-3 text-red-600 font-semibold" data-testid="employer-cancel-late-warning">
                                        Heads up: this is within 48 hours of the trial start. You will not be refunded.
                                    </p>
                                )}
                            </div>
                            <div className="flex justify-end space-x-3">
                                <Button variant="outline" onClick={() => setEmployerCancelModal(null)} className="rounded-full" disabled={cancellingEmployer}>
                                    Keep booking
                                </Button>
                                <Button onClick={handleEmployerCancelConfirm} disabled={cancellingEmployer}
                                    className="rounded-full bg-red-500 hover:bg-red-600 text-white"
                                    data-testid="confirm-employer-cancel-btn">
                                    {cancellingEmployer ? 'Cancelling…' : 'Cancel trial'}
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Review & Pay Modal */}
            {paymentModal && (() => {
                const total = paymentModal.total;
                const totalPence = Math.round(total * 100);
                const applied = paymentModal.applyCredit
                    ? Math.min(paymentModal.creditPence, totalPence)
                    : 0;
                const remainingPence = Math.max(totalPence - applied, 0);
                const remainingDisplay = `£${(remainingPence / 100).toFixed(2)}`;
                const appliedDisplay = `£${(applied / 100).toFixed(2)}`;
                const totalDisplay = `£${total.toFixed(2)}`;
                const fullyCovered = remainingPence === 0;
                return (
                    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => !payingBookingId && setPaymentModal(null)}>
                        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="payment-modal">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Review &amp; Pay</h3>
                                <button onClick={() => !payingBookingId && setPaymentModal(null)} className="text-slate-400 hover:text-slate-600" data-testid="payment-modal-close">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="mb-4">
                                <p className="text-sm text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                    {paymentModal.booking.candidate_name} &middot; {paymentModal.booking.job_title}
                                </p>
                                <p className="text-xs text-slate-400 mt-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                    {paymentModal.booking.trial_days} day{paymentModal.booking.trial_days !== 1 ? 's' : ''} &middot; Start {formatDate(paymentModal.booking.start_date)}
                                </p>
                            </div>
                            <div className="rounded-xl border border-purple-200/60 p-4 space-y-2 mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Trial cost</span>
                                    <span className="text-[#0A0A0A] font-semibold">{totalDisplay}</span>
                                </div>
                                {paymentModal.creditPence > 0 && (
                                    <label className="flex items-center justify-between gap-3 text-sm cursor-pointer pt-2 border-t border-purple-100" data-testid="apply-credit-toggle">
                                        <span className="flex items-center gap-2 text-slate-600">
                                            <input
                                                type="checkbox"
                                                checked={paymentModal.applyCredit}
                                                onChange={(e) => setPaymentModal(m => ({ ...m, applyCredit: e.target.checked }))}
                                                className="accent-[#7C3AED]"
                                                data-testid="apply-credit-checkbox"
                                            />
                                            Apply platform credit (£{(paymentModal.creditPence / 100).toFixed(2)} available)
                                        </span>
                                        <span className="text-[#10B981] font-semibold">−{appliedDisplay}</span>
                                    </label>
                                )}
                                <div className="flex justify-between text-base pt-2 border-t border-purple-100">
                                    <span className="text-[#0A0A0A] font-semibold">{fullyCovered ? 'Total due' : 'Due via Stripe'}</span>
                                    <span className="text-[#0A0A0A] font-bold" data-testid="payment-modal-amount-due">{remainingDisplay}</span>
                                </div>
                            </div>
                            <Button
                                onClick={handleConfirmPayment}
                                disabled={!!payingBookingId}
                                className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white hover:opacity-90"
                                data-testid="confirm-payment-btn"
                            >
                                {payingBookingId
                                    ? 'Processing…'
                                    : fullyCovered
                                        ? 'Confirm with credit'
                                        : `Pay ${remainingDisplay} via Stripe`}
                            </Button>
                        </div>
                    </div>
                );
            })()}

            {/* Delete Account Modal */}
            {showDeleteAccount && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setShowDeleteAccount(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-account-modal">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <Trash2 className="h-5 w-5 text-red-500" />
                            </div>
                            <h3 className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Delete Account</h3>
                        </div>
                        <p className="text-slate-500 mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            Are you sure you want to permanently delete your account? This will remove all your data including job postings, trials, and shortlists. This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <Button variant="outline" onClick={() => setShowDeleteAccount(false)} className="rounded-full">Cancel</Button>
                            <Button onClick={handleDeleteAccount} disabled={deletingAccount}
                                className="rounded-full bg-red-500 hover:bg-red-600 text-white"
                                data-testid="confirm-delete-account-btn">
                                {deletingAccount ? 'Deleting...' : 'Delete My Account'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyDashboard;
